import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

export const getSellableItems = createAsyncThunk(
  "salesOrder/getSellableItems",
  async (payload = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.append("IsSaleable", true);
      params.append("IsDiscontinued", true); // for get discontuned item also
      if (payload.searchText) {
        params.append("searchText", payload.searchText);
      }
      params.append("PageNumber", payload.PageNumber || 1);
      params.append("PageSize", payload.PageSize || 100);

      // Add location parameters for location-specific stock filtering
      if (payload.DeliveryLocationId) {
        params.append("DeliveryLocationId", payload.DeliveryLocationId);
      }
      if (payload.DeliveryLocationType) {
        params.append("DeliveryLocationType", payload.DeliveryLocationType);
      }

      const { data } = await axios.get(
        `${API_URL.ITEM.ACTIVE_ITEMS_LOOKUP}?${params.toString()}`,
      );
      return data.data.list;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Fetch stock for multiple items at a specific delivery location
// Used when delivery location changes to update stock for already-selected items
export const getItemsStockByLocation = createAsyncThunk(
  "salesOrder/getItemsStockByLocation",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.STOCK.ITEMS_STOCK_BY_LOCATION,
        payload,
      );
      return data.data; // Returns { itemId1: stock1, itemId2: stock2, ... }
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getSOBulkImportHistory = createAsyncThunk(
  "salesOrder/getSOBulkImportHistory",
  async (payload, { rejectWithValue }) => {
    try {
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.post(
        `${API_URL.SALES_ORDER.BULK_IMPORT_HISTORY}?${queryString}`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const bulkImportSalesOrders = createAsyncThunk(
  "salesOrder/bulkImportSalesOrders",
  async (
    { file, templateId, businessLocationId, warehouseId },
    { rejectWithValue },
  ) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const params = new URLSearchParams();
      if (templateId) params.append("templateId", templateId);
      if (businessLocationId)
        params.append("businessLocationId", businessLocationId);
      if (warehouseId) params.append("warehouseId", warehouseId);

      const queryString = params.toString();
      const url = queryString
        ? `${API_URL.SALES_ORDER.BULK_IMPORT}?${queryString}`
        : API_URL.SALES_ORDER.BULK_IMPORT;

      const data = await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 5 * 60 * 1000, // 5 minutes for large files
      });
      return data.data;
    } catch (error) {
      // return rejectWithValue(error.data);
      // Handle network errors (no response from server)
      if (!error?.data && !error?.response) {
        return rejectWithValue({
          message:
            error?.message ||
            "Network error. The file may be too large or the server timed out.",
          errors: [],
        });
      }
      // error.data is the response body: { data: { errors: [...], message: "..." }, isSuccess: true }
      // We need to pass error.data.data to get the inner object with errors
      const errorPayload = error?.data?.data || error?.data || error;
      return rejectWithValue(errorPayload);
    }
  },
);

// Resolve a SKU/Quantity spreadsheet into priced line items for the items grid.
// Stateless and synchronous — nothing is persisted; the resolved rows are bound
// into the form for review. Distinct from bulkImportSalesOrders (whole orders).
export const resolveItemsFromFile = createAsyncThunk(
  "salesOrder/resolveItemsFromFile",
  async (
    { file, customerId, sourceOfSupplyStateId, destinationOfSupplyStateId },
    { rejectWithValue },
  ) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const params = new URLSearchParams();
      if (customerId) params.append("customerId", customerId);
      if (sourceOfSupplyStateId)
        params.append("sourceOfSupplyStateId", sourceOfSupplyStateId);
      if (destinationOfSupplyStateId)
        params.append("destinationOfSupplyStateId", destinationOfSupplyStateId);

      const response = await axios.post(
        `${API_URL.SALES_ORDER.RESOLVE_ITEMS_FROM_FILE}?${params.toString()}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 5 * 60 * 1000, // 5 minutes for large files
        },
      );
      // response.data is the API envelope { data, isSuccess, message };
      // the resolve payload is the inner .data.
      return response.data?.data;
    } catch (error) {
      // Network errors (no response from server)
      if (!error?.data && !error?.response) {
        return rejectWithValue({
          message:
            error?.message ||
            "Network error. The file may be too large or the server timed out.",
        });
      }
      const errorPayload = error?.data?.data || error?.data || error;
      return rejectWithValue(errorPayload);
    }
  },
);

export const getNextSONumber = createAsyncThunk(
  "salesOrder/getNextSONumber",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.SALES_ORDER.GET_NEXT_SO_NUMBER);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getSalesOrders = createAsyncThunk(
  "salesOrder/getSalesOrders",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.SALES_ORDER.SLUG}?${queryString}`,
      );
      const sales_order_list = data.data.list;
      const totalRecords = data.data.totalRecords;
      return { sales_order_list, data: { totalRecords } };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const createSalesOrder = createAsyncThunk(
  "salesOrder/createSalesOrder",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.SALES_ORDER.SLUG, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const updateSalesOrder = createAsyncThunk(
  "salesOrder/updateSalesOrder",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(API_URL.SALES_ORDER.SLUG, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const deleteSalesOrder = createAsyncThunk(
  "salesOrder/deleteSalesOrder",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete(`${API_URL.SALES_ORDER.SLUG}/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getSalesOrderById = createAsyncThunk(
  "salesOrder/getSalesOrderById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL.SALES_ORDER.SLUG}/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const resolveDiscountSchemes = createAsyncThunk(
  "salesOrder/resolveDiscountSchemes",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.DISCOUNT_SCHEME.RESOLVE,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getSalesOrderItems = createAsyncThunk(
  "salesOrder/getSalesOrderItems",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.SALES_ORDER.GET_ITEMS(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const submitForApproval = createAsyncThunk(
  "salesOrder/submitForApproval",
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.SALES_ORDER.SUBMIT_FOR_APPROVAL}/${id}`,
      );
      if (getState().salesOrder.sales_order_detail?.id === id) {
        dispatch(getSalesOrderById(id));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const approveSalesOrder = createAsyncThunk(
  "salesOrder/approveSalesOrder",
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.put(`${API_URL.SALES_ORDER.APPROVE}/${id}`);
      if (getState().salesOrder.sales_order_detail?.id === id) {
        dispatch(getSalesOrderById(id));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const rejectSalesOrder = createAsyncThunk(
  "salesOrder/rejectSalesOrder",
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.put(`${API_URL.SALES_ORDER.REJECT}/${id}`);
      if (getState().salesOrder.sales_order_detail?.id === id) {
        dispatch(getSalesOrderById(id));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const confirmSalesOrder = createAsyncThunk(
  "salesOrder/confirmSalesOrder",
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.put(`${API_URL.SALES_ORDER.CONFIRM}/${id}`);
      if (getState().salesOrder.sales_order_detail?.id === id) {
        dispatch(getSalesOrderById(id));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const cancelSalesOrder = createAsyncThunk(
  "salesOrder/cancelSalesOrder",
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.put(`${API_URL.SALES_ORDER.CANCEL}/${id}`);
      if (getState().salesOrder.sales_order_detail?.id === id) {
        dispatch(getSalesOrderById(id));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const reopenSalesOrder = createAsyncThunk(
  "salesOrder/reopenSalesOrder",
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.put(`${API_URL.SALES_ORDER.REOPEN}/${id}`);
      if (getState().salesOrder.sales_order_detail?.id === id) {
        dispatch(getSalesOrderById(id));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getSalesPersons = createAsyncThunk(
  "salesOrder/getSalesPersons",
  async (payload = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.append("pageNumber", payload.pageNumber || 1);
      params.append("pageSize", payload.pageSize || 10);
      if (payload.searchText) {
        params.append("searchText", payload.searchText);
      }
      if (payload.salesOrderId) {
        params.append("salesOrderId", payload.salesOrderId);
      }

      const { data } = await axios.get(
        `${API_URL.SALES_ORDER.GET_SALES_PERSONS}?${params.toString()}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getOrganisationUsers = createAsyncThunk(
  "salesOrder/getOrganisationUsers",
  async (payload = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.append(
        "pageNumber",
        payload.PageNumber || payload.pageNumber || 1,
      );
      params.append("pageSize", payload.PageSize || payload.pageSize || 30);
      if (payload.searchText) {
        params.append("searchText", payload.searchText);
      }

      const { data } = await axios.get(
        `${API_URL.SALES_ORDER.GET_ORGANISATION_USERS}?${params.toString()}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getPaymentTerms = createAsyncThunk(
  "salesOrder/getPaymentTerms",
  async (payload = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.append("PageNumber", payload.PageNumber || 1);
      params.append("PageSize", payload.PageSize || 50);
      if (payload.SearchText) {
        params.append("SearchText", payload.SearchText);
      }

      const { data } = await axios.get(
        `${API_URL.PAYMENT_TERMS.ACTIVE}?${params.toString()}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getCancelPreview = createAsyncThunk(
  "salesOrder/getCancelPreview",
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.SALES_ORDER.CANCEL_PREVIEW(salesOrderId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const cancelSalesOrderItems = createAsyncThunk(
  "salesOrder/cancelSalesOrderItems",
  async (
    { salesOrderId, payload },
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      const { data } = await axios.post(
        API_URL.SALES_ORDER.CANCEL_ITEMS(salesOrderId),
        payload,
      );
      if (getState().salesOrder.sales_order_detail?.id === salesOrderId) {
        dispatch(getSalesOrderById(salesOrderId));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const voidSalesOrder = createAsyncThunk(
  "salesOrder/voidSalesOrder",
  async ({ salesOrderId, reason }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.post(
        API_URL.SALES_ORDER.VOID(salesOrderId),
        { reason },
      );
      if (getState().salesOrder.sales_order_detail?.id === salesOrderId) {
        dispatch(getSalesOrderById(salesOrderId));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const closeSalesOrder = createAsyncThunk(
  "salesOrder/closeSalesOrder",
  async (
    { salesOrderId, remarks },
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      const { data } = await axios.post(
        API_URL.SALES_ORDER.CLOSE(salesOrderId),
        { remarks },
      );
      if (getState().salesOrder.sales_order_detail?.id === salesOrderId) {
        dispatch(getSalesOrderById(salesOrderId));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getCancelledItems = createAsyncThunk(
  "salesOrder/getCancelledItems",
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.SALES_ORDER.GET_CANCELLED_ITEMS(salesOrderId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getPicklistsBySalesOrder = createAsyncThunk(
  "salesOrder/getPicklistsBySalesOrder",
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.PICKLIST.BY_SALES_ORDER(salesOrderId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getPackagesBySalesOrder = createAsyncThunk(
  "salesOrder/getPackagesBySalesOrder",
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.PACKAGE.BY_SALES_ORDER(salesOrderId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getShipmentsBySalesOrder = createAsyncThunk(
  "salesOrder/getShipmentsBySalesOrder",
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.SHIPMENT.BY_SALES_ORDER(salesOrderId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getInvocesBySalesOrder = createAsyncThunk(
  "salesOrder/getInvocesBySalesOrder",
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.INVOICE.BY_SALES_ORDER(salesOrderId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getSalesReturnsBySalesOrder = createAsyncThunk(
  "salesOrder/getSalesReturnsBySalesOrder",
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.SALES_RETURN.BY_SALES_ORDER(salesOrderId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getDeliveryChallansBySalesOrder = createAsyncThunk(
  "salesOrder/getDeliveryChallansBySalesOrder",
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.DELIVERY_CHALLAN.BY_SALES_ORDER(salesOrderId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getBackordersBySalesOrder = createAsyncThunk(
  "salesOrder/getBackordersBySalesOrder",
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.BACKORDER.GET_BY_SALES_ORDER(salesOrderId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

const initialState = {
  sales_order_list: [],
  sales_order_detail: null,
  sales_order_items: [],
  so_item_loading: false,
  cancelled_items: [],
  cancelled_items_loaded: false,
  cancelled_items_loading: false,
  picklists_for_so: [],
  picklists_loaded: false,
  picklists_loading: false,
  packages_for_so: [],
  packages_loaded: false,
  packages_loading: false,
  shipments_for_so: [],
  shipments_loaded: false,
  shipments_loading: false,
  invoices_for_so: [],
  invoices_loaded: false,
  invoices_loading: false,
  sales_returns_for_so: [],
  sales_returns_loaded: false,
  sales_returns_loading: false,
  backorders_for_so: [],
  backorders_loaded: false,
  backorders_loading: false,
  delivery_challans_for_so: [],
  delivery_challans_loaded: false,
  delivery_challans_loading: false,
  sellable_items: [],
  current_values: null,
  active_payload: null,
  total_records: 0,
  next_so_number: null,
  payment_terms_list: [],
  organisation_users: [],
  detail_loading: false,
  bulk_import_history: [],
};

const salesOrderSlice = createSlice({
  name: "salesOrder",
  initialState,
  reducers: {
    setCurrentValues(state, action) {
      state.current_values = action.payload;
    },
    setActivePayload(state, action) {
      state.active_payload = action.payload;
    },
    resetCurrentValue(state) {
      state.current_values = null;
      state.sales_order_list = [];
    },
    // Clears only the edit-session seed. Safe to call on AddComponent mount
    // without wiping the cached list used by ListComponent's skip-fetch path.
    resetCurrentValueOnly(state) {
      state.current_values = null;
    },
    setSellableItems(state, action) {
      state.sellable_items = action.payload;
    },
    resetCancelledItems(state) {
      state.cancelled_items = [];
      state.cancelled_items_loaded = false;
      state.cancelled_items_loading = false;
    },
    resetPicklistsForSO(state) {
      state.picklists_for_so = [];
      state.picklists_loaded = false;
      state.picklists_loading = false;
    },
    resetPackagesForSO(state) {
      state.packages_for_so = [];
      state.packages_loaded = false;
      state.packages_loading = false;
    },
    resetShipmentsForSO(state) {
      state.shipments_for_so = [];
      state.shipments_loaded = false;
      state.shipments_loading = false;
    },
    resetInvoicesForSO(state) {
      state.invoices_for_so = [];
      state.invoices_loaded = false;
      state.invoices_loading = false;
    },
    resetSalesReturnsForSO(state) {
      state.sales_returns_for_so = [];
      state.sales_returns_loaded = false;
      state.sales_returns_loading = false;
    },
    resetBackordersForSO(state) {
      state.backorders_for_so = [];
      state.backorders_loaded = false;
      state.backorders_loading = false;
    },
    resetDeliveryChallansForSO(state) {
      state.delivery_challans_for_so = [];
      state.delivery_challans_loaded = false;
      state.delivery_challans_loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSalesOrders.fulfilled, (state, action) => {
        state.sales_order_list = action.payload.sales_order_list;
        state.total_records = action.payload.data?.totalRecords || 0;
      })
      .addCase(getSalesOrderById.pending, (state) => {
        state.detail_loading = true;
      })
      .addCase(getSalesOrderById.fulfilled, (state, action) => {
        state.sales_order_detail = action.payload;
        state.detail_loading = false;
      })
      .addCase(getSalesOrderById.rejected, (state) => {
        state.detail_loading = false;
      })
      .addCase(getSalesOrderItems.pending, (state) => {
        state.so_item_loading = true;
      })
      .addCase(getSalesOrderItems.fulfilled, (state, action) => {
        state.sales_order_items = action.payload;
        state.so_item_loading = false;
      })
      .addCase(getSalesOrderItems.rejected, (state) => {
        state.so_item_loading = false;
      })
      .addCase(getSellableItems.fulfilled, (state, action) => {
        const pageNumber = action.meta.arg?.PageNumber || 1;
        if (pageNumber === 1) {
          // Replace items on first page (new filter or initial load)
          state.sellable_items = action.payload;
        } else {
          // Append for pagination
          state.sellable_items = [...state.sellable_items, ...action.payload];
        }
      })
      .addCase(getNextSONumber.fulfilled, (state, action) => {
        state.next_so_number = action.payload;
      })
      .addCase(getPaymentTerms.fulfilled, (state, action) => {
        state.payment_terms_list = action.payload?.list || action.payload || [];
      })
      .addCase(getOrganisationUsers.fulfilled, (state, action) => {
        const list = action.payload?.list || [];
        const pageNumber = action.meta.arg?.PageNumber || 1;
        if (pageNumber > 1) {
          const combinedList = [...state.organisation_users, ...list];
          const uniqueList = Array.from(
            new Map(combinedList.map((item) => [item.id, item])).values(),
          );
          state.organisation_users = uniqueList;
        } else {
          state.organisation_users = list;
        }
      })
      .addCase(getCancelledItems.pending, (state) => {
        state.cancelled_items_loading = true;
      })
      .addCase(getCancelledItems.fulfilled, (state, action) => {
        state.cancelled_items = action.payload?.items || [];
        state.cancelled_items_loaded = true;
        state.cancelled_items_loading = false;
      })
      .addCase(getCancelledItems.rejected, (state) => {
        state.cancelled_items = [];
        state.cancelled_items_loaded = true;
        state.cancelled_items_loading = false;
      })
      .addCase(cancelSalesOrderItems.fulfilled, (state) => {
        // Reset so the Cancelled Items tab re-fetches fresh data
        state.cancelled_items_loaded = false;
      })
      .addCase(getPicklistsBySalesOrder.pending, (state) => {
        state.picklists_loading = true;
      })
      .addCase(getPicklistsBySalesOrder.fulfilled, (state, action) => {
        state.picklists_for_so = action.payload || [];
        state.picklists_loaded = true;
        state.picklists_loading = false;
      })
      .addCase(getPicklistsBySalesOrder.rejected, (state) => {
        state.picklists_for_so = [];
        state.picklists_loaded = true;
        state.picklists_loading = false;
      })
      .addCase(getPackagesBySalesOrder.pending, (state) => {
        state.packages_loading = true;
      })
      .addCase(getPackagesBySalesOrder.fulfilled, (state, action) => {
        state.packages_for_so = action.payload || [];
        state.packages_loaded = true;
        state.packages_loading = false;
      })
      .addCase(getPackagesBySalesOrder.rejected, (state) => {
        state.packages_for_so = [];
        state.packages_loaded = true;
        state.packages_loading = false;
      })
      .addCase(getShipmentsBySalesOrder.pending, (state) => {
        state.shipments_loading = true;
      })
      .addCase(getShipmentsBySalesOrder.fulfilled, (state, action) => {
        state.shipments_for_so = action.payload || [];
        state.shipments_loaded = true;
        state.shipments_loading = false;
      })
      .addCase(getShipmentsBySalesOrder.rejected, (state) => {
        state.shipments_for_so = [];
        state.shipments_loaded = true;
        state.shipments_loading = false;
      })
      .addCase(getInvocesBySalesOrder.pending, (state) => {
        state.invoices_loading = true;
      })
      .addCase(getInvocesBySalesOrder.fulfilled, (state, action) => {
        state.invoices_for_so = action.payload || [];
        state.invoices_loaded = true;
        state.invoices_loading = false;
      })
      .addCase(getInvocesBySalesOrder.rejected, (state) => {
        state.invoices_for_so = [];
        state.invoices_loaded = true;
        state.invoices_loading = false;
      })
      .addCase(getSalesReturnsBySalesOrder.pending, (state) => {
        state.sales_returns_loading = true;
      })
      .addCase(getSalesReturnsBySalesOrder.fulfilled, (state, action) => {
        state.sales_returns_for_so = action.payload || [];
        state.sales_returns_loaded = true;
        state.sales_returns_loading = false;
      })
      .addCase(getSalesReturnsBySalesOrder.rejected, (state) => {
        state.sales_returns_for_so = [];
        state.sales_returns_loaded = true;
        state.sales_returns_loading = false;
      })
      .addCase(getBackordersBySalesOrder.pending, (state) => {
        state.backorders_loading = true;
      })
      .addCase(getBackordersBySalesOrder.fulfilled, (state, action) => {
        state.backorders_for_so = action.payload || [];
        state.backorders_loaded = true;
        state.backorders_loading = false;
      })
      .addCase(getBackordersBySalesOrder.rejected, (state) => {
        state.backorders_for_so = [];
        state.backorders_loaded = true;
        state.backorders_loading = false;
      })
      .addCase(getDeliveryChallansBySalesOrder.pending, (state) => {
        state.delivery_challans_loading = true;
      })
      .addCase(getDeliveryChallansBySalesOrder.fulfilled, (state, action) => {
        state.delivery_challans_for_so = action.payload || [];
        state.delivery_challans_loaded = true;
        state.delivery_challans_loading = false;
      })
      .addCase(getDeliveryChallansBySalesOrder.rejected, (state) => {
        state.delivery_challans_for_so = [];
        state.delivery_challans_loaded = true;
        state.delivery_challans_loading = false;
      })
      .addCase(getSOBulkImportHistory.fulfilled, (state, action) => {
        state.bulk_import_history = action.payload.list || [];
      });
  },
});

export const {
  setCurrentValues,
  setActivePayload,
  resetCurrentValue,
  resetCurrentValueOnly,
  setSellableItems,
  resetCancelledItems,
  resetPicklistsForSO,
  resetPackagesForSO,
  resetShipmentsForSO,
  resetInvoicesForSO,
  resetSalesReturnsForSO,
  resetBackordersForSO,
  resetDeliveryChallansForSO,
} = salesOrderSlice.actions;
export default salesOrderSlice.reducer;
